import { computed, watch } from 'vue';
import { catchError, EMPTY } from 'rxjs';
import { isEqual } from 'lodash';

import {
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import type {
  PortStaticInfo,
  Dictionary,
  TransportVehicle,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import {
  AutoCompleteSearchType,
  CategoryType,
  IngredientType,
  SearchTermType,
  TransportVehicleType,
} from '@libs/trip-planner-interfaces';
import { assertValue, exhaustiveGuard, isDefined } from '@libs/utils';
import {
  deserializeConstraint,
  isFullTextConstraint,
} from '@libs/constraint-builder';

import type TripPlannerModel from '../tripPlannerModel/TripPlannerModel';
import type { HotelIngredient } from '../recipe/ingredients';
import type { OwnStayIngredient } from '../recipe/ingredients/ownStayIngredient';
import { isHotelIngredient } from '../recipe/ingredients/typeGuards';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useRecipeViewModel(model: TripPlannerModel) {
  const statsigEventsStore = useStatsigEventsStore();
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();
  const recipeIatas = computed(() =>
    recipeStore.flightOnlyRecipe.flatMap((flight) => {
      assertValue(flight.fromPort, 'RecipeViewModel > recipeIatas > fromPort');
      assertValue(flight.toPort, 'RecipeViewModel > recipeIatas > toPort');
      return [flight.fromPort, flight.toPort];
    })
  );
  watch(
    recipeIatas,
    (iatas) => {
      tripStore.updateSearchingFlightsStaticInfo(true);

      model.searchAirportStaticInfo(iatas).subscribe({
        next: (ports: Dictionary<PortStaticInfo>) => {
          tripStore.insertPortStaticInfo(ports, IngredientType.Flight);
          tripStore.setFlightStaticInfoToRecipe();
        },
        complete: () => {
          tripStore.updateSearchingFlightsStaticInfo(false);
        },
      });
    },
    { immediate: true }
  );

  const recipeSeaports = computed(() =>
    recipeStore.ferryOnlyRecipe.flatMap((ferry) => {
      assertValue(
        ferry.fromPort,
        'RecipeViewModel > recipeSeaports > fromPort'
      );
      assertValue(ferry.toPort, 'RecipeViewModel > recipeSeaports > toPort');
      return [ferry.fromPort, ferry.toPort];
    })
  );
  watch(
    recipeSeaports,
    (seaports) => {
      tripStore.updateSearchingFerriesStaticInfo(true);

      model.searchSeaportStaticInfo(seaports).subscribe({
        next: (ports: Dictionary<PortStaticInfo>) => {
          tripStore.insertPortStaticInfo(ports, IngredientType.Ferry);
          tripStore.setFerryStaticInfoToRecipe();
        },
        complete: () => {
          tripStore.updateSearchingFerriesStaticInfo(false);
        },
        error: () => tripStore.updateSearchingFerriesStaticInfo(false),
      });
    },
    { immediate: true }
  );

  model.getFerryVehicleTypes().subscribe((response) =>
    tripStore.updateFerriesVehicleTypes(
      response.reduce((acc: TransportVehicle[], vehicle: TransportVehicle) => {
        if (vehicle.id === TransportVehicleType.None) {
          acc.unshift(vehicle);
        } else {
          acc.push(vehicle);
        }

        return acc;
      }, [])
    )
  );

  const recipeStaySearchCriterias = computed(() =>
    recipeStore.stayOnlyRecipe.map((stayIngredient) => {
      const { ingredientId, searchTerm } = stayIngredient;

      return {
        ingredientId,
        searchTerm,
      };
    })
  );
  watch(
    recipeStaySearchCriterias,
    (newStaySearchCriterias, oldStaySearchCriterias) => {
      newStaySearchCriterias.forEach(({ ingredientId, searchTerm }) => {
        const stayIngredient = recipeStore.getStayIngredient(ingredientId);

        const oldSearchCriteria = oldStaySearchCriterias?.find(
          (oldSearchCriteria) => oldSearchCriteria.ingredientId === ingredientId
        );

        const hasChangedSearchTerm = !isEqual(
          searchTerm,
          oldSearchCriteria?.searchTerm
        );

        updateStayPosition(stayIngredient);
        updateDisplayText(stayIngredient);

        if (
          hasChangedSearchTerm &&
          searchTerm.type === SearchTermType.FreeText
        ) {
          updatePolygonId(stayIngredient, searchTerm.freeText);
          updateHotelCode(stayIngredient, searchTerm.freeText);
        }

        if (
          hasChangedSearchTerm &&
          searchTerm.type === SearchTermType.HotelCode &&
          isHotelIngredient(stayIngredient)
        ) {
          updateNearbyPolygon(stayIngredient);
        }

        if (
          hasChangedSearchTerm &&
          searchTerm.type === SearchTermType.PolygonId
        ) {
          updateDestinationInformation(searchTerm.polygonId, ingredientId);
        }
      });
    },
    { immediate: true }
  );

  function updateStayPosition(
    ingredient: HotelIngredient | OwnStayIngredient
  ): void {
    model
      .searchHotelPosition(ingredient.getSearchText())
      .subscribe(
        (hotelPositionResult) =>
          (ingredient.positionFromSearchText = hotelPositionResult)
      );
  }

  function updateDisplayText(
    stayIngredient: HotelIngredient | OwnStayIngredient
  ): void {
    const hasSerializedConstraintSearchText =
      stayIngredient.searchTerm.type === SearchTermType.FreeText &&
      !isFullTextConstraint(
        deserializeConstraint(stayIngredient.searchTerm.freeText).selection
      );

    const hasOnlyPolygonId =
      stayIngredient.searchTerm.type === SearchTermType.PolygonId;

    const hasOnlyHotelCode =
      stayIngredient.searchTerm.type === SearchTermType.HotelCode;

    const hasSameDisplayText =
      stayIngredient.searchTerm.type === SearchTermType.FreeText &&
      stayIngredient.searchTerm.freeText === stayIngredient.displayText;

    if (
      (hasSerializedConstraintSearchText ||
        hasOnlyPolygonId ||
        hasOnlyHotelCode) &&
      (!stayIngredient.displayText || hasSameDisplayText)
    ) {
      switch (stayIngredient.searchTerm.type) {
        case SearchTermType.HotelCode:
          model
            .searchHotelName(stayIngredient.getSearchText())
            .subscribe((response) => {
              stayIngredient.displayText = response;
            });
          break;

        case SearchTermType.PolygonId:
          model
            .searchAutoCompleteSuggestions({
              searchType: AutoCompleteSearchType.Destination,
              searchText: stayIngredient.getSearchText(),
            })
            .subscribe((suggestionCategories) => {
              const suggestedPolygon = suggestionCategories.filter(
                (suggestionCategory) =>
                  suggestionCategory.type === CategoryType.PolygonCityZone
              )[0]?.data[0];

              stayIngredient.displayText = suggestedPolygon?.label ?? '';
            });
          break;

        case SearchTermType.FreeText:
          model
            .searchHotelMainPolygon(stayIngredient.searchTerm.freeText)
            .subscribe(({ name }) => {
              stayIngredient.displayText = name ?? '';
            });
          break;

        default:
          exhaustiveGuard(stayIngredient.searchTerm, 'Invalid searchTerm type');
      }
    }
  }

  function updatePolygonId(
    stayIngredient: HotelIngredient | OwnStayIngredient,
    ingredientSearchText: string
  ): void {
    model
      .searchAutoCompleteSuggestions({
        searchType: AutoCompleteSearchType.Destination,
        searchText: ingredientSearchText,
      })
      .subscribe((suggestionCategories) => {
        const suggestedPolygon = suggestionCategories.filter(
          (suggestionCategory) =>
            suggestionCategory.type === CategoryType.PolygonCityZone
        )[0]?.data[0];

        const hasSuggestedPolygonId =
          !!suggestedPolygon && 'polygonId' in suggestedPolygon;

        if (
          hasSuggestedPolygonId &&
          suggestedPolygon.label === ingredientSearchText
        ) {
          recipeStore.updateShouldSkipItinerarySearch(true);

          stayIngredient.setSearchTerm({
            type: SearchTermType.PolygonId,
            polygonId: suggestedPolygon.polygonId,
          });
        }
      });
  }

  function updateHotelCode(
    stayIngredient: HotelIngredient | OwnStayIngredient,
    ingredientSearchText: string
  ): void {
    model.searchHotelCode(ingredientSearchText).subscribe((hotelCode) => {
      if (isDefined(hotelCode)) {
        recipeStore.updateShouldSkipItinerarySearch(true);

        stayIngredient.setSearchTerm({
          type: SearchTermType.HotelCode,
          hotelCode,
        });
      }
    });
  }

  function updateNearbyPolygon(hotelIngredient: HotelIngredient): void {
    model
      .searchHotelMainPolygon(hotelIngredient.getSearchText())
      .subscribe(
        ({ name, id }) => (hotelIngredient.nearbyPolygon = { name, id })
      );
  }

  function updateDestinationInformation(
    polygonId: number,
    ingredientId: IngredientId
  ): void {
    model
      .searchDestinationInformation(polygonId)
      .pipe(
        catchError(() => {
          statsigEventsStore.logMissingDestinationInformation({
            polygonId,
            missingReason: 'NotFound',
          });

          return EMPTY;
        })
      )
      .subscribe((destinationInformation) => {
        const hasMissingDescription = !destinationInformation.description;
        const hasMissingImage = !destinationInformation.image;

        if (hasMissingDescription || hasMissingImage) {
          statsigEventsStore.logMissingDestinationInformation({
            polygonId,
            missingReason: hasMissingDescription
              ? 'MissingDescription'
              : 'MissingImage',
          });
        }

        tripStore.addDestinationInformation(
          destinationInformation,
          ingredientId
        );
      });
  }
}
